import { useState, useEffect } from "react";
import ManagerLayout from "@/components/layout/ManagerLayout";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, queryClient } from "@/lib/queryClient";
import { apiRequest } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Copy, Link, Trash, Edit, Pencil, Trash2 } from "lucide-react";
import { formatDistance, format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/hooks/use-auth";

export default function ManagerLinksPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<string>("");
  const [markupPercentage, setMarkupPercentage] = useState<string>("5");
  const [currentEditLink, setCurrentEditLink] = useState<any>(null);
  const [allPropertiesLink, setAllPropertiesLink] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 640 : true
  );

  useEffect(() => {
    function handleResize() {
      setIsDesktop(window.innerWidth >= 640);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch all active properties for link creation
  const { data: properties, isLoading: isLoadingProperties } = useQuery({
    queryKey: ["active-properties"],
    queryFn: getQueryFn(),
  });

  // Fetch manager links
  const {
    data: links,
    isLoading: isLoadingLinks,
    isError: isLinksError,
  } = useQuery({
    queryKey: ["manager/links"],
    queryFn: getQueryFn(),
    // refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Create a new link
  const createLinkMutation = useMutation({
    mutationFn: async (newLink: { propertyId: number; markupPercentage: number; allProperties?: boolean }) => {
      const res = await apiRequest('POST', 'manager/links', newLink);
      // return await res.json();
      return res;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["manager/links"] });
      setIsCreatingLink(false);
      setSelectedProperty("");
      setMarkupPercentage("5");
      setAllPropertiesLink(false);
      
      // Show success message with the correct link format
      let linkUrl;
      if (data.allProperties) {
        linkUrl = `${window.location.origin}/properties/link/${data.linkCode}`;
      } else {
        linkUrl = `${window.location.origin}/properties/${data.propertyId}/link/${data.linkCode}`;
      }
      
      toast({
        title: "Link created successfully!",
        description: (
          <div className="mt-2">
            <p className="mb-2">Your property link has been created. You can copy it below:</p>
            <div className="flex items-center gap-2 bg-muted p-2 rounded-md">
              <code className="text-sm flex-1">{linkUrl}</code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(linkUrl);
                  toast({
                    title: "Link copied",
                    description: "The link has been copied to clipboard.",
                  });
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ),
        duration: 10000, // Show for 10 seconds
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create link",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update an existing link
  const updateLinkMutation = useMutation({
    mutationFn: async ({ id, markupPercentage }: { id: number; markupPercentage: number }) => {
      const res = await apiRequest("PATCH", `manager/links/${id}`, { markupPercentage });
      // return await res.json();
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["manager/links"] });
      setIsEditingLink(false);
      setCurrentEditLink(null);
      toast({
        title: "Link updated",
        description: "Your property link has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update link",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete a link
  const deleteLinkMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `manager/links/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["manager/links"] });
      toast({
        title: "Link deleted",
        description: "Your property link has been deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete link",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Handle create link
  const handleCreateLink = () => {
    // Check if this is an "all properties" link
    if (allPropertiesLink) {
      const markup = parseFloat(markupPercentage);
      if (isNaN(markup) || markup < 0 || markup > 100) {
        toast({
          title: "Invalid markup",
          description: "Please enter a valid markup percentage between 0 and 100.",
          variant: "destructive",
        });
        return;
      }

      createLinkMutation.mutate({
        propertyId: 0, // Special value for all properties
        markupPercentage: markup,
        allProperties: true
      });
      return;
    }
    
    // Regular single property link
    if (!selectedProperty) {
      toast({
        title: "Select a property",
        description: "Please select a property to create a link for.",
        variant: "destructive",
      });
      return;
    }

    const markup = parseFloat(markupPercentage);
    if (isNaN(markup) || markup < 0 || markup > 100) {
      toast({
        title: "Invalid markup",
        description: "Please enter a valid markup percentage between 0 and 100.",
        variant: "destructive",
      });
      return;
    }

    createLinkMutation.mutate({
      propertyId: parseInt(selectedProperty),
      markupPercentage: markup,
    });
  };

  // Handle edit link
  const handleUpdateLink = () => {
    if (!currentEditLink) return;

    const markup = parseFloat(markupPercentage);
    if (isNaN(markup) || markup < 0 || markup > 100) {
      toast({
        title: "Invalid markup",
        description: "Please enter a valid markup percentage between 0 and 100.",
        variant: "destructive",
      });
      return;
    }

    updateLinkMutation.mutate({
      id: currentEditLink.id,
      markupPercentage: markup,
    });
  };

  // Copy link to clipboard
  const copyLinkToClipboard = (link: any) => {
    let url;
    if (link.allProperties) {
      // Universal link - no specific property ID
      url = `${window.location.origin}/properties/link/${link.linkCode}`;
    } else {
      // Individual property link - include property ID
      url = `${window.location.origin}/properties/${link.propertyId}/link/${link.linkCode}`;
    }
    
    navigator.clipboard.writeText(url).then(
      () => {
        toast({
          title: "Link copied",
          description: "The link has been copied to clipboard.",
        });
      },
      () => {
        toast({
          title: "Failed to copy",
          description: "Could not copy the link to clipboard.",
          variant: "destructive",
        });
      }
    );
  };

  // Prepare for editing
  const startEditLink = (link: any) => {
    setCurrentEditLink(link);
    setMarkupPercentage(link.markupPercentage.toString());
    setIsEditingLink(true);
  };

  // Find property by id
  const getPropertyById = (id: number) => {
    if (!properties) return null;
    return properties.find((p: any) => p.id === id);
  };

  // Loading state
  if (isLoadingLinks) {
    return (
      <ManagerLayout>
        <Helmet>
          <title>Property Links | StayMiddle</title>
        </Helmet>
        <div className="container py-10">
          <h1 className="text-3xl font-bold mb-8">My PropertyLinks</h1>
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </ManagerLayout>
    );
  }

  // Error state
  if (isLinksError) {
    return (
      <ManagerLayout>
        <Helmet>
          <title>Property Links | StayMiddle</title>
        </Helmet>
        <div className="container py-10">
          <h1 className="text-3xl font-bold mb-8">My PropertyLinks</h1>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <h3 className="text-xl font-bold mb-2">Failed to load links</h3>
                <p className="text-muted-foreground mb-4">
                  There was an error loading your links. Please try again.
                </p>
                <Button
                  onClick={() => queryClient.invalidateQueries({ queryKey: ["manager/links"] })}
                >
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </ManagerLayout>
    );
  }

  return (
    <ManagerLayout>
      <Helmet>
        <title>Property Links | StayMiddle</title>
      </Helmet>
      <div className="container py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Property Links</h1>
            <p className="text-muted-foreground mt-1">
              Create and manage custom links for your properties
            </p>
          </div>
          <Dialog open={isCreatingLink} onOpenChange={setIsCreatingLink}>
            <DialogTrigger asChild>
              <Button className="ml-auto">Create New Link</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create new property link</DialogTitle>
                <DialogDescription>
                  Generate a unique link with your custom markup to share with clients or agents.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="all-properties"
                      checked={allPropertiesLink}
                      onCheckedChange={(checked) => {
                        setAllPropertiesLink(checked === true);
                        if (checked) {
                          setSelectedProperty("");
                        }
                      }}
                    />
                    <label
                      htmlFor="all-properties"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Create link for all properties
                    </label>
                  </div>
                </div>
                
                {!allPropertiesLink && (
                  <div className="grid gap-2">
                    <Label htmlFor="property">Property</Label>
                    <Select
                      value={selectedProperty}
                      onValueChange={setSelectedProperty}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a property" />
                      </SelectTrigger>
                      <SelectContent>
                        {isLoadingProperties ? (
                          <div className="flex justify-center items-center p-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        ) : properties && properties.length > 0 ? (
                          properties.map((property: any) => (
                            <SelectItem key={property.id} value={property.id.toString()}>
                              {property.title} ({property.area})
                            </SelectItem>
                          ))
                        ) : (
                          <div className="text-sm p-2 text-center text-muted-foreground">
                            No properties available
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <div className="grid gap-2">
                  <Label htmlFor="markup">Markup percentage</Label>
                  <div className="flex items-center">
                    <Input
                      id="markup"
                      type="number"
                      min="0"
                      max="100"
                      value={markupPercentage}
                      onChange={(e) => setMarkupPercentage(e.target.value)}
                      className="w-full"
                    />
                    <span className="ml-2">%</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This is how much will be added to the base price (1-100%)
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setIsCreatingLink(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateLink}
                  disabled={createLinkMutation.isPending}
                >
                  {createLinkMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Link"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Link Dialog */}
          <Dialog open={isEditingLink} onOpenChange={setIsEditingLink}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit property link</DialogTitle>
                <DialogDescription>
                  Update the markup percentage for this link.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="property">Property</Label>
                  <Input 
                    value={currentEditLink ? getPropertyById(currentEditLink.propertyId)?.title : ""}
                    disabled
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="markup">Markup percentage</Label>
                  <div className="flex items-center">
                    <Input
                      id="markup"
                      type="number"
                      min="0"
                      max="100"
                      value={markupPercentage}
                      onChange={(e) => setMarkupPercentage(e.target.value)}
                      className="w-full"
                    />
                    <span className="ml-2">%</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This is how much will be added to the base price (1-100%)
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setIsEditingLink(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleUpdateLink}
                  disabled={updateLinkMutation.isPending}
                >
                  {updateLinkMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Link"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {links && links.length > 0 ? (
          <Card>
            <CardContent className="pt-6">
              {/* Table for desktop only */}
              {isDesktop && (
                <div className="w-full overflow-x-auto">
                  <Table className="min-w-[600px]">
                    <TableCaption>List of your custom property links</TableCaption>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Property</TableHead>
                        <TableHead>Link Code</TableHead>
                        <TableHead>Markup</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {links.map((link: any) => {
                        const property = getPropertyById(link.propertyId);
                        let linkUrl;
                        if (link.allProperties) {
                          linkUrl = `${window.location.origin}/properties/link/${link.linkCode}`;
                        } else {
                          linkUrl = `${window.location.origin}/properties/${link.propertyId}/link/${link.linkCode}`;
                        }
                        
                        return (
                          <TableRow key={link.id}>
                            <TableCell>
                              <div className="font-medium">
                                {link.allProperties ? "All Properties" : (property?.title || "Unknown Property")}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {link.allProperties ? "Universal link for all properties" : (property?.area || "")}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center space-x-2">
                                <div className="flex-1 min-w-0">
                                  <div className="truncate font-mono text-sm">{linkUrl}</div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => copyLinkToClipboard(link)}
                                  title="Copy link"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => window.open(linkUrl, '_blank')}
                                  title="Open link in new tab"
                                >
                                  <Link className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell>{link.markupPercentage}%</TableCell>
                            <TableCell>{format(new Date(link.createdAt), 'MMM d, yyyy')}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => startEditLink(link)}
                                  title="Edit markup"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    if (window.confirm("Are you sure you want to delete this link?")) {
                                      deleteLinkMutation.mutate(link.id);
                                    }
                                  }}
                                  title="Delete link"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
              {/* Card layout for mobile only */}
              {!isDesktop && (
                <div className="flex flex-col gap-4">
                  {links.map((link: any) => {
                    const property = getPropertyById(link.propertyId);
                    let linkUrl;
                    if (link.allProperties) {
                      linkUrl = `${window.location.origin}/properties/link/${link.linkCode}`;
                    } else {
                      linkUrl = `${window.location.origin}/properties/${link.propertyId}/link/${link.linkCode}`;
                    }
                    return (
                      <div key={link.id} className="border rounded-lg p-4 bg-white shadow-sm flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="font-semibold text-base">
                              {link.allProperties ? "All Properties" : (property?.title || "Unknown Property")}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {link.allProperties ? "Universal link for all properties" : (property?.area || "")}
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">{format(new Date(link.createdAt), 'MMM d, yyyy')}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-mono text-xs truncate flex-1">{linkUrl}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => copyLinkToClipboard(link)}
                            title="Copy link"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => window.open(linkUrl, '_blank')}
                            title="Open link in new tab"
                          >
                            <Link className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-xs bg-muted px-2 py-1 rounded">Markup: {link.markupPercentage}%</span>
                          <div className="flex gap-2 ml-auto">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => startEditLink(link)}
                              title="Edit markup"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                if (window.confirm("Are you sure you want to delete this link?")) {
                                  deleteLinkMutation.mutate(link.id);
                                }
                              }}
                              title="Delete link"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Link className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">No links created yet</h3>
                <p className="text-muted-foreground mb-4">
                  Create your first property link to start sharing with clients or agents.
                </p>
                <Button onClick={() => setIsCreatingLink(true)}>
                  Create Your First Link
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* About Property Links section - mobile friendly */}
        <div className="mt-8 bg-muted/50 rounded-lg p-6 border">
          <h3 className="text-lg font-semibold mb-2">About Property Links</h3>
          <p className="text-muted-foreground mb-4">
            Property links allow you to create custom URLs for your properties with unique markup percentages. When 
            clients book through these links, the amount displayed includes your markup. You can create multiple links 
            with different markups for the same property.
          </p>
          <div className="flex flex-col gap-4 mt-4 md:flex-row md:gap-6">
            <div className="flex items-start space-x-3">
              <div className="mt-0.5 bg-primary/10 text-primary p-2 rounded-full">
                <Copy className="h-4 w-4" />
              </div>
              <div>
                <h4 className="font-medium">Easy Sharing</h4>
                <p className="text-sm text-muted-foreground">
                  Share links via email, text message, or social media with one click.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="mt-0.5 bg-primary/10 text-primary p-2 rounded-full">
                <Edit className="h-4 w-4" />
              </div>
              <div>
                <h4 className="font-medium">Custom Markup</h4>
                <p className="text-sm text-muted-foreground">
                  Set different markup percentages for different marketing channels.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ManagerLayout>
  );
}